import { CommunicationDataType, GameDataDefinition } from '@toilet-paper-games/core';

import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { ArrowClockwise } from 'react-bootstrap-icons';

import { IFrameLoader, IFrameLoaderRef } from '@/common/components/iframe/IFrameLoader';
import { NetworkDataType } from '@/common/models/NetworkDataTransfers';
import { CommunicationHandlerAppHoster } from '@/hoster/models/CommunicationHandlerAppHoster';
import { GameInfo } from '@/types';
import {
  Button,
  FlexRow,
  Intent,
  absolute,
  fullSize,
  margin,
  standardSizes,
} from '@toilet-paper-games/ui';

import { BaseViewModel, useViewModelConstructor } from '@/common/utils/mobx/ViewModel';
import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import {
  HosterAppModel,
  useHosterAppModel,
  useHosterNetworkHandler,
} from '../models/HosterAppModel';
import { HosterNetworkHandler } from '../models/HosterNetworkHandler';
import { autorun, toJS } from 'mobx';
import { HosterInjectedGameDataHandler } from '../models/HosterInjectedGameDataHandler';

export interface HosterGamePlayerViewModelProps {
  game: GameInfo;
  appModel: HosterAppModel;
  iFrameLoaderRef: React.RefObject<IFrameLoaderRef>;
  networkHandler: HosterNetworkHandler;
  lobbyGame: boolean;
  updateRefreshCount: () => void;
}

export class HosterGamePlayerViewModel extends BaseViewModel<HosterGamePlayerViewModelProps> {
  communicationHandler: CommunicationHandlerAppHoster<GameDataDefinition> =
    new CommunicationHandlerAppHoster(this);

  hosterInjectedGameDataHandler: HosterInjectedGameDataHandler;

  constructor(props: HosterGamePlayerViewModelProps) {
    super(props);
    makeSimpleAutoObservable(this, {}, { autoBind: true });

    this.hosterInjectedGameDataHandler = new HosterInjectedGameDataHandler(
      this.props.game,
      this.props.appModel,
      this.props.lobbyGame,
    );
  }

  get appModel() {
    return this.props.appModel;
  }

  setupHosterInjectedGameDataHandler() {
    return this.hosterInjectedGameDataHandler.setup();
  }

  setupCommunicationHandler() {
    const iFrameLoader = this.props.iFrameLoaderRef.current;

    if (!iFrameLoader) {
      throw new Error('No iframe loader');
    }

    const iframe = iFrameLoader.iframe;

    if (!iframe) {
      throw new Error('No iframe');
    }

    const messenger = (data: unknown) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(data, '*');
      }
    };

    this.communicationHandler.setupMessenger(messenger);

    return () => {};
  }

  setupCommunicationListeners() {
    const communicationHandler = this.communicationHandler;
    const networkHandler = this.props.networkHandler;

    const refreshListenerDestroyer = networkHandler.addDataListener(() => {
      this.reload();
      networkHandler.broadcast({
        type: NetworkDataType.REFRESH,
        data: null,
      });
    }, NetworkDataType.REFRESH);

    let startupAutoRunDisposer: (() => void) | undefined;
    const startupListener = communicationHandler.addAppMessageListener((data) => {
      if (startupAutoRunDisposer) startupAutoRunDisposer();
      console.log(`Game running core version: ${data.data.coreVersion}`);

      startupAutoRunDisposer = autorun(() => {
        communicationHandler.sendToFrame({
          type: CommunicationDataType.AppData_HOSTER,
          data: toJS(this.hosterInjectedGameDataHandler.injectedGameData),
        });
      });
    }, CommunicationDataType.INIT_GAME_HOSTER);

    const readyListener = communicationHandler.addAppMessageListener(({ data }) => {
      this.hosterInjectedGameDataHandler.ready = data.ready;

      // TODO: change this to a reaction/autorun in view model or injectedGameDataHandler
      // where it will also re-send if a new player joins too
      networkHandler.broadcast({
        type: NetworkDataType.GAME_READY_STATUS_HOSTER_H2C,
        data: { ready: this.hosterInjectedGameDataHandler.ready },
      });
    }, CommunicationDataType.READY_STATUS_HOSTER);

    const controllerPongListenerDisposer = networkHandler.addDataListener((message) => {
      communicationHandler.sendToFrame({
        type: CommunicationDataType.PONG_CONTROLLER,
        data: {
          controllerTime: message.data.controllerTime,
          id: message.data.id,
          playerId: message.data.userId,
        },
      });
    }, NetworkDataType.PONG_C2H);

    const gameActionListenerDisposer = networkHandler.addDataListener((listener) => {
      communicationHandler.sendToFrame({
        type: CommunicationDataType.GAME_ACTION_RESPONSE_HOSTER,
        data: { payload: listener.data, from: listener.userId },
      });
    }, NetworkDataType.GAME_ACTION);

    const gameReadyStatusControllerDisposer = networkHandler.addDataListener(
      (listener) => {
        const user = networkHandler.userManager.usersMap.get(listener.userId);

        if (!user) {
          throw new Error('User not found');
        }

        if (!this.hosterInjectedGameDataHandler.playerModelHandlersMap) {
          throw new Error('Player model handlers not found');
        }

        const player = this.hosterInjectedGameDataHandler.playerModelHandlersMap.get(
          listener.userId,
        );

        if (!player) {
          throw new Error('Player not found');
        }

        player.playerModel.ready = listener.data.ready;

        const connection = user.connection;

        if (!connection) {
          throw new Error('Connection not found');
        }

        // TODO: Check if this is necessary
        connection.send({
          type: NetworkDataType.GAME_READY_STATUS_HOSTER_H2C,
          data: { ready: this.hosterInjectedGameDataHandler.ready },
        });
      },
      NetworkDataType.GAME_READY_STATUS_CONTROLLER_C2H,
    );

    const gameStorageUpdateListener = communicationHandler.addAppMessageListener(
      ({ data }) => {
        this.hosterInjectedGameDataHandler.updateGameStorage(data.key, data.value);

        communicationHandler.sendToFrame({
          type: CommunicationDataType.UPDATED_GAME_STORAGE_HOSTER_P2G,
          data: { key: data.key, value: data.value },
        });
      },
      CommunicationDataType.SET_GAME_STORAGE_HOSTER_G2P,
    );

    const globalSettingsUpdateListener = communicationHandler.addAppMessageListener(
      ({ data }) => {
        this.appModel.updateGlobalSettings(data.key, data.value);

        communicationHandler.sendToFrame({
          type: CommunicationDataType.UPDATED_GLOBAL_SETTING_HOSTER_P2G,
          data: { key: data.key, value: data.value } as any,
        });
      },
      CommunicationDataType.SET_GLOBAL_SETTING_HOSTER_G2P,
    );

    return () => {
      refreshListenerDestroyer();
      startupListener.destroy();
      if (startupAutoRunDisposer) startupAutoRunDisposer();

      readyListener.destroy();
      controllerPongListenerDisposer();
      gameActionListenerDisposer();
      gameReadyStatusControllerDisposer();

      gameStorageUpdateListener.destroy();
      globalSettingsUpdateListener.destroy();
    };
  }

  onMessage(message: string) {
    this.communicationHandler.messageHandler(message);
  }

  reload() {
    const iFrameLoader = this.props.iFrameLoaderRef.current;

    if (!iFrameLoader) {
      throw new Error('No iframe loader');
    }

    iFrameLoader.reload();

    this.props.updateRefreshCount();
  }
}

export interface GamePlayerProps {
  game: GameInfo;
  noButtons?: boolean;
  lobbyGame: boolean;
}

export const HosterGamePlayer = observer(
  ({ game, noButtons, lobbyGame }: GamePlayerProps) => {
    const appModel = useHosterAppModel();
    const networkHandler = useHosterNetworkHandler();

    const iFrameLoaderRef = useRef<IFrameLoaderRef>(null);
    const [refreshCount, setRefreshCount] = useState(0);

    const updateRefreshCount = () => {
      setRefreshCount((count) => count + 1);
    };

    const viewModel = useViewModelConstructor(HosterGamePlayerViewModel, {
      game,
      appModel,
      iFrameLoaderRef,
      networkHandler,
      lobbyGame,
      updateRefreshCount,
    });

    useEffect(() => {
      return viewModel.setupHosterInjectedGameDataHandler();
    }, [viewModel, refreshCount]);

    useEffect(() => {
      const setupHandlerDisposer = viewModel.setupCommunicationHandler();
      const setupListenerDisposer = viewModel.setupCommunicationListeners();

      return () => {
        setupHandlerDisposer();
        setupListenerDisposer();
      };
    }, []);

    const showRefreshButton =
      appModel.forceShowExitButton ||
      // !game.extra?.handleGameExit ||
      appModel.networkHandler.userManager.connectedUsers.length === 0;

    return (
      <div css={fullSize}>
        {!noButtons && showRefreshButton && <RefreshButton reloader={viewModel.reload} />}
        <div css={[fullSize]}>
          <IFrameLoader
            ref={iFrameLoaderRef}
            title=""
            onMessage={viewModel.onMessage}
            src={game.endpoints.hoster}
          />
        </div>
      </div>
    );
  },
);

export interface DevModeButtonProps {
  reloader: () => void;
}

/** Auto handles sending out broadcast to connection to refresh */
export const RefreshButton = observer((props: DevModeButtonProps) => {
  const { reloader } = props;

  const networkHandler = useHosterNetworkHandler();
  const appStore = useHosterAppModel();

  return (
    <div css={[absolute(0, 0), margin('sm'), { zIndex: 200 }]}>
      <FlexRow gap={standardSizes.md}>
        <Button
          onClick={appStore.endGame}
          intent={Intent.Danger}
          children="END"
          buttonHeight={standardSizes.xs}
        />
        <Button
          buttonHeight={standardSizes.xs}
          onClick={() => {
            networkHandler.broadcast({
              type: NetworkDataType.REFRESH,
              data: null,
            });
            reloader();
          }}
        >
          <ArrowClockwise />
        </Button>
      </FlexRow>
    </div>
  );
});
